import { APP_INITIALIZER, Inject, NgModule, ViewChild } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AngularMaterialModule } from './angular-material.module';
import { LangChangeEvent, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MainComponent } from './main/main.component';
import { LayoutModule } from '@angular/cdk/layout';
import { ReceiptsComponent } from './documents/receipts/receipts.component';
import { PrintersComponent } from './administration/printers/printer.component';
import { SignInComponent } from './auth/sign-in/sign-in.component';
import { HomeComponent } from './home/home.component';
import { AuthComponent } from './auth/auth.component';
import { ForbiddenComponent } from './auth/forbidden/forbidden.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { HelpDialogComponent } from './main/help-dialog/help-dialog.component';
import { SalesStatementComponent } from './reports/sales-statement/sales-statement.component';
import { ReportsComponent } from './reports/reports.component';
import { PrintersStatusComponent } from './reports/printers-status/printers-status.component';
import { QueueStatusComponent } from './reports/queue-status/queue-status.component';
import { ReportIssueComponent } from './report-issue/report-issue.component';
import { TranslationService } from './_common/services/translation.service';
import { AppHttpInterceptor } from './app.interceptor';
import { appInitializer, AuthService } from './auth/auth.service';
import { DailyReportsComponent } from './documents/daily-reports/daily-reports.component';
import { NonFiscalsComponent } from './documents/non-fiscals/non-fiscals.component';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { ToastrModule } from 'ngx-toastr';
import { UserComponent } from './administration/users/user.component';
import { AdvancedFiltersComponent } from './_common/components/filters/advanced-filters/advanced-filters.component';
import { TableFiltersComponent } from './_common/components/filters/table-filters/table-filters.component';
import { TimeMaskDirective } from './_common/directives/time-mask.directive';
import { NumberMaskDirective } from './_common/directives/number-mask.directive';
import { AmountMaskDirective } from './_common/directives/amount-mask.directive';
import { NumberAndLetterMaskDirective } from './_common/directives/number-and-letter-mask.directive';
import { ClickOutsideDirective } from './_common/directives/click-outside.directive';
import { ShopComponent } from './administration/shops/shop.component';
import { ActionButtonsComponent } from './main/action-buttons/action-buttons.component';
import { UserAddOrEditDialog } from './administration/users/add-or-edit-dialog/user-add-or-edit.dialog';
import { ShopAddOrEditDialog } from './administration/shops/add-or-edit-dialog/shop-add-or-edit.dialog';
import { PrinterAddOrEditDialog } from './administration/printers/add-or-edit-dialog/printer-add-or-edit.dialog';
import { PrinterHistoryDialog } from './administration/printers/printer-history-dialog/printer-history.dialog';
import { PreviewDialog } from './documents/preview-dialog/preview.dialog';
import { LegalNoticeDialogComponent } from './main/legal-notice-dialog/legal-notice-dialog.component';
import { GdprDialogComponent } from './main/gdpr-dialog/gdpr-dialog.component';
import { PipesModule } from './_common/pipes/pipes.module';
import { DashCasePipe } from './_common/pipes/dash-case-pipe';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { SelectCheckAllComponent } from './_common/components/select-check-all/select-check-all.component';
import { APP_BASE_HREF, DatePipe, DecimalPipe, PlatformLocation } from '@angular/common';
import { SendDialog } from './documents/preview-dialog/send-dialog/send-dialog.component';
import { AngularResizeEventModule } from 'angular-resize-event';
import { MatTooltipDefaultOptions, MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DATE_TIME_FORMAT } from './_common/constants';
import { NgxMaskModule } from "ngx-mask";
import '@angular/common/locales/global/pl';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorIntlExtension } from './_common/extensions/paginator.extension';
import { MtxDatetimepickerIntlExtension } from './_common/extensions/datetimepicker.extension';
import { LanguageComponent } from './_common/components/language/language.component';
import { NumberRangeComponent } from './_common/components/filters/number-range-input/number-range-input.component';
import { TimeRangeComponent } from './_common/components/filters/time-range-input/time-range-input.component';
import { MtxNativeDatetimeModule } from '@ng-matero/extensions/core';
import { MtxDatetimepickerModule, MtxDatetimepickerIntl } from '@ng-matero/extensions/datetimepicker';
import { MomentDatetimeAdapter, MtxMomentDatetimeModule } from '@ng-matero/extensions-moment-adapter';
import { BrandComponent } from './main/brand/brand.component';
import { BrandService } from './administration/brands/brand.service';
import { ImageService } from './_common/services/image.service';
import { environment } from 'src/environments/environment';
import { AuthenticationResult, BrowserCacheLocation, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalService } from '@azure/msal-angular';
import { ConfigService } from './_common/services/config.service';

export function dynamicTranslationLoaderFactory(translationService: TranslationService) {
  return new DynamicTranslateLoader(translationService);
}

export class DynamicTranslateLoader implements TranslateLoader {
  constructor(private translationService: TranslationService) { }

  public getTranslation(lang: string): any {
    return this.translationService.getTranslation(lang);
  }
}

export function getApiUrl() {
  return environment.apiUrl;
}

export function getAzureCredentials() {
  return environment.azureCredentials;
}


export function MsalInstanceFactory(): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: getAzureCredentials().clientIdUi,
      authority: `https://login.microsoftonline.com/${getAzureCredentials().tenantId}`,
      redirectUri: getAzureCredentials().redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
      storeAuthStateInCookie: false,
    }
  })
}

export function MsalInterceptorConfigFactory() {
  const apiUrl = getApiUrl();
  const azureCredentials = getAzureCredentials();
  const scope = `api://${azureCredentials.clientIdApi}/${azureCredentials.scopeName}`;

  const protectedResourceMap = new Map<string, Array<string>>([
    [`${apiUrl}auth/authenticate`, [scope]],
    [`${apiUrl}brand/brand-list`, [scope]],
    [`${apiUrl}salesStatement/summary`, [scope]],
    [`${apiUrl}salesStatement/download-csv`, [scope]],
    [`${apiUrl}salesStatement/download-pdf`, [scope]],
    [`${apiUrl}salesStatement/download-xlsx`, [scope]],
    [`${apiUrl}salesStatement/basedOnItems/download-csv`, [scope]],
    [`${apiUrl}dailyReport/daily-reports`, [scope]],
    [`${apiUrl}receipt/receipts`, [scope]],
    [`${apiUrl}PrinterStatus/printer-status`, [scope]],
    [`${apiUrl}shop/shops`, [scope]],
    [`${apiUrl}printer/printers`, [scope]]
  ]);
  
  return protectedResourceMap;
}

const msalProviders = [];
const msalImports = [];

if (environment.authMode === 'Entra') {
  msalProviders.push(
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  );

  msalImports.push(
    MsalModule.forRoot(
      MsalInstanceFactory(),
      {
        interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
        authRequest: {
          scopes: ['User.Read'], // Scopes required by the Microsoft Graph API
        }
      },
      {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: MsalInterceptorConfigFactory(),
      }
    ));
}

const tooltipDefaultOptions: MatTooltipDefaultOptions = {
  showDelay: 800, // Set the delay in milliseconds
  hideDelay: 0,
  touchendHideDelay: 0,
};

@NgModule({
  declarations: [
    //DIALOGS
    HelpDialogComponent,
    LegalNoticeDialogComponent,
    GdprDialogComponent,
    UserAddOrEditDialog,
    ShopAddOrEditDialog,
    PrinterAddOrEditDialog,
    PrinterHistoryDialog,
    PreviewDialog,
    SendDialog,

    AuthComponent,
    ForbiddenComponent,
    SignInComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    AppComponent,
    MainComponent,
    HomeComponent,
    ReceiptsComponent,
    PrintersComponent,
    SalesStatementComponent,
    ReportsComponent,
    PrintersStatusComponent,
    QueueStatusComponent,
    ReportIssueComponent,
    DailyReportsComponent,
    NonFiscalsComponent,
    UserComponent,
    TableFiltersComponent,
    ShopComponent,
    ActionButtonsComponent,
    SelectCheckAllComponent,
    AdvancedFiltersComponent,
    NumberRangeComponent,
    TimeRangeComponent,

    //DIRECTIVES
    NumberMaskDirective,
    AmountMaskDirective,
    NumberAndLetterMaskDirective,
    TimeMaskDirective,
    ClickOutsideDirective,
    LanguageComponent,
    BrandComponent,

  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    AngularMaterialModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskModule.forRoot(),
    ToastrModule.forRoot({
      positionClass: 'toast-top-center'
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: dynamicTranslationLoaderFactory,
        deps: [TranslationService]
      }
    }),
    LayoutModule,
    PipesModule,
    FontAwesomeModule,
    AngularResizeEventModule,
    InfiniteScrollModule,    
    MtxDatetimepickerModule,    
    MtxNativeDatetimeModule, 
    MtxMomentDatetimeModule,
    msalImports
  ],
  providers: [
    { provide: APP_BASE_HREF, useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(), deps: [PlatformLocation] },
    { provide: APP_INITIALIZER, useFactory: appInitializer, multi: true, deps: [AuthService, TranslationService, BrandService, ConfigService] },
    { provide: HTTP_INTERCEPTORS, useClass: AppHttpInterceptor, multi: true },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: tooltipDefaultOptions },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MomentDatetimeAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: DATE_TIME_FORMAT },
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlExtension },
    { provide: MtxDatetimepickerIntl, useClass: MtxDatetimepickerIntlExtension },
    DatePipe,
    DecimalPipe,
    DashCasePipe,
    msalProviders
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(
    private translate: TranslateService,
    private adapter: DateAdapter<Date>,
    private timeAdapter: MomentDatetimeAdapter,
  ) {
    this.translate.onLangChange.subscribe(({ lang }: LangChangeEvent) => {
      this.adapter.setLocale(lang);
      this.adapter.getFirstDayOfWeek = () => (lang === 'en' ? 0 : 1);
      this.timeAdapter.setLocale(lang);
    })
  }
}
